import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

const inputSt  = { width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const smInput  = { ...inputSt, padding: '6px 10px', fontSize: 12 };
const focusSt  = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
const blurSt   = e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; };

const emptyForm      = { name: '', description: '' };
const emptyChecklist = { name: '', description: '', required: true, required_for_advance: false, order: 0 };
const emptyStatus    = { name: '', order: 0 };
const sortByOrder    = list => [...(list || [])].sort((a, b) => a.order - b.order);

const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children} {required && <span style={{ color: '#EF4444' }}>*</span>}
  </label>
);

const IconBtn = ({ onClick, title, variant = 'default', children }) => {
  const v = { default: ['#F4F6FB', '#4B5563', '#E5E7EB'], primary: ['#EEF0FB', '#4F63D2', '#dde1f7'], danger: ['#FEE2E2', '#EF4444', '#fecaca'], info: ['#DBEAFE', '#1D4ED8', '#bfdbfe'] }[variant];
  return (
    <button onClick={onClick} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer', backgroundColor: v[0], color: v[1], flexShrink: 0 }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = v[2]}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = v[0]}>
      {children}
    </button>
  );
};

const EditIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = ({ size = 12 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const GripIcon = () => (
  <svg width="12" height="12" fill="currentColor" viewBox="0 0 20 20">
    <circle cx="6" cy="5" r="1.4" /><circle cx="14" cy="5" r="1.4" />
    <circle cx="6" cy="10" r="1.4" /><circle cx="14" cy="10" r="1.4" />
    <circle cx="6" cy="15" r="1.4" /><circle cx="14" cy="15" r="1.4" />
  </svg>
);

const DuplicateIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const ProcedureTypes = () => {
  const [types, setTypes]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editing, setEditing]           = useState(null);
  const [expandedType, setExpandedType] = useState(null);
  const [submitting, setSubmitting]     = useState(false);
  const [form, setForm]                 = useState(emptyForm);
  const [checklistForm, setChecklistForm]       = useState(emptyChecklist);
  const [showChecklistForm, setShowChecklistForm] = useState(null);
  const [editingChecklist, setEditingChecklist]   = useState(null);
  const [expandedStatusType, setExpandedStatusType] = useState(null);
  const [statusForm, setStatusForm]             = useState(emptyStatus);
  const [showStatusForm, setShowStatusForm]     = useState(null);
  const [editingStatus, setEditingStatus]       = useState(null);
  const [dragStatusItem, setDragStatusItem]     = useState(null); // { typeId, itemId }
  const [syncing, setSyncing]                   = useState(null);
  const [duplicating, setDuplicating]           = useState(null);
  const [duplicateForm, setDuplicateForm]       = useState(emptyForm);
  const [duplicateSelected, setDuplicateSelected] = useState({});

  const fetchTypes = async () => {
    try { const res = await api.get('/procedure-types'); setTypes(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleNew    = () => { setDuplicating(null); setEditing(null); setForm(emptyForm); setShowForm(true); };
  const handleEdit   = t  => { setDuplicating(null); setEditing(t); setForm({ name: t.name, description: t.description || '' }); setShowForm(true); };
  const handleCancel = () => { setShowForm(false); setEditing(null); setForm(emptyForm); };

  const handleDuplicate = type => {
    setShowForm(false); setEditing(null);
    setDuplicating(type);
    setDuplicateForm({ name: `Copia de ${type.name}`, description: type.description || '' });
    const sel = {};
    (type.document_checklists || []).forEach(d => { sel[d.id] = true; });
    setDuplicateSelected(sel);
  };
  const handleCancelDuplicate = () => { setDuplicating(null); setDuplicateForm(emptyForm); setDuplicateSelected({}); };
  const toggleDuplicateDoc    = id => setDuplicateSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSubmitDuplicate = async e => {
    e.preventDefault(); setSubmitting(true);
    try {
      const res = await api.post('/procedure-types', { name: duplicateForm.name, description: duplicateForm.description });
      const newType = res.data;
      const docs = sortByOrder(duplicating.document_checklists).filter(d => duplicateSelected[d.id]);
      let order = 1;
      for (const doc of docs) {
        await api.post(`/procedure-types/${newType.id}/document-checklists`, {
          name: doc.name, description: doc.description || '', required: doc.required, order: order++,
        });
      }
      toast.success('Tipo duplicado');
      handleCancelDuplicate(); fetchTypes();
    } catch (e) { toast.error('Ocurrió un error al duplicar'); }
    finally { setSubmitting(false); }
  };

  const handleSubmit = async e => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) await api.put(`/procedure-types/${editing.id}`, form);
      else await api.post('/procedure-types', form);
      toast.success(editing ? 'Tipo actualizado' : 'Tipo creado');
      handleCancel(); fetchTypes();
    } catch (e) { toast.error('Ocurrió un error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Desactivar este tipo de trámite?')) return;
    try { await api.delete(`/procedure-types/${id}`); toast.success('Tipo desactivado'); fetchTypes(); }
    catch (e) { toast.error('No se pudo desactivar'); }
  };

  const handleNewChecklist      = typeId => {
    setEditingChecklist(null);
    const items = types.find(t => t.id === typeId)?.document_checklists || [];
    const nextOrder = items.length ? Math.max(...items.map(i => i.order || 0)) + 1 : 1;
    setChecklistForm({ ...emptyChecklist, order: nextOrder });
    setShowChecklistForm(typeId);
  };
  const handleEditChecklist     = (item, typeId) => { setEditingChecklist(item); setChecklistForm({ name: item.name, description: item.description || '', required: item.required, required_for_advance: item.required_for_advance || false, order: item.order }); setShowChecklistForm(typeId); };
  const handleCancelChecklist   = () => { setShowChecklistForm(null); setEditingChecklist(null); setChecklistForm(emptyChecklist); };

  const handleSubmitChecklist = async (e, typeId) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editingChecklist) await api.put(`/procedure-types/${typeId}/document-checklists/${editingChecklist.id}`, checklistForm);
      else await api.post(`/procedure-types/${typeId}/document-checklists`, checklistForm);
      toast.success(editingChecklist ? 'Documento actualizado' : 'Documento agregado');
      handleCancelChecklist(); fetchTypes();
    } catch (e) { toast.error('Ocurrió un error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteChecklist = async (typeId, checklistId) => {
    if (!window.confirm('¿Eliminar este documento del checklist?')) return;
    try { await api.delete(`/procedure-types/${typeId}/document-checklists/${checklistId}`); toast.success('Documento eliminado'); fetchTypes(); }
    catch (e) { toast.error('No se pudo eliminar'); }
  };

  const [dragItem, setDragItem] = useState(null); // { typeId, itemId }

  const handleDrop = async (typeId, targetItemId) => {
    if (!dragItem || dragItem.typeId !== typeId || dragItem.itemId === targetItemId) { setDragItem(null); return; }
    const type = types.find(t => t.id === typeId);
    const items = sortByOrder(type?.document_checklists);
    const fromIdx = items.findIndex(i => i.id === dragItem.itemId);
    const toIdx = items.findIndex(i => i.id === targetItemId);
    setDragItem(null);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    const reordered = items.map((item, idx) => ({ ...item, order: idx + 1 }));
    setTypes(prev => prev.map(t => t.id === typeId ? { ...t, document_checklists: reordered } : t));
    try {
      await Promise.all(reordered.map(item => api.put(`/procedure-types/${typeId}/document-checklists/${item.id}`, { order: item.order })));
    } catch (e) {
      toast.error('No se pudo guardar el nuevo orden');
      fetchTypes();
    }
  };

  const handleSyncChecklist = async typeId => {
    if (!window.confirm('¿Aplicar "Necesario para avanzar" del checklist a los expedientes ya existentes de este tipo? Si algún expediente ya tiene esos documentos entregados, puede avanzar de etapa automáticamente.')) return;
    setSyncing(typeId);
    try {
      const res = await api.post(`/procedure-types/${typeId}/sync-checklist`);
      const { documents_updated, case_files_advanced } = res.data;
      const advancedVerb = case_files_advanced === 1 ? 'avanzó' : 'avanzaron';
      toast.success(`${documents_updated} documento${documents_updated !== 1 ? 's' : ''} actualizado${documents_updated !== 1 ? 's' : ''}${case_files_advanced > 0 ? ` · ${case_files_advanced} expediente${case_files_advanced !== 1 ? 's' : ''} ${advancedVerb} de etapa` : ''}`);
    } catch (e) { toast.error('No se pudo sincronizar'); }
    finally { setSyncing(null); }
  };

  const handleNewStatus      = typeId => {
    setEditingStatus(null);
    const items = types.find(t => t.id === typeId)?.statuses || [];
    const nextOrder = items.length ? Math.max(...items.map(i => i.order || 0)) + 1 : 1;
    setStatusForm({ ...emptyStatus, order: nextOrder });
    setShowStatusForm(typeId);
  };
  const handleEditStatus     = (item, typeId) => { setEditingStatus(item); setStatusForm({ name: item.name, order: item.order }); setShowStatusForm(typeId); };
  const handleCancelStatus   = () => { setShowStatusForm(null); setEditingStatus(null); setStatusForm(emptyStatus); };

  const handleSubmitStatus = async (e, typeId) => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editingStatus) await api.put(`/procedure-types/${typeId}/statuses/${editingStatus.id}`, statusForm);
      else await api.post(`/procedure-types/${typeId}/statuses`, statusForm);
      toast.success(editingStatus ? 'Estatus actualizado' : 'Estatus agregado');
      handleCancelStatus(); fetchTypes();
    } catch (e) { toast.error('Ocurrió un error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteStatus = async (typeId, statusId) => {
    if (!window.confirm('¿Eliminar este estatus?')) return;
    try { await api.delete(`/procedure-types/${typeId}/statuses/${statusId}`); toast.success('Estatus eliminado'); fetchTypes(); }
    catch (e) { toast.error(e.response?.data?.message || 'No se pudo eliminar'); }
  };

  const handleDropStatus = async (typeId, targetItemId) => {
    if (!dragStatusItem || dragStatusItem.typeId !== typeId || dragStatusItem.itemId === targetItemId) { setDragStatusItem(null); return; }
    const type = types.find(t => t.id === typeId);
    const items = sortByOrder(type?.statuses);
    const fromIdx = items.findIndex(i => i.id === dragStatusItem.itemId);
    const toIdx = items.findIndex(i => i.id === targetItemId);
    setDragStatusItem(null);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    const reordered = items.map((item, idx) => ({ ...item, order: idx + 1 }));
    setTypes(prev => prev.map(t => t.id === typeId ? { ...t, statuses: reordered } : t));
    try {
      await Promise.all(reordered.map(item => api.put(`/procedure-types/${typeId}/statuses/${item.id}`, { order: item.order })));
    } catch (e) {
      toast.error('No se pudo guardar el nuevo orden');
      fetchTypes();
    }
  };

  return (
    <div>
      <PageHeader
        title="Tipos de Trámite"
        subtitle="Define los tipos de trámite y sus documentos requeridos"
        actions={
          <button onClick={handleNew}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d4fb8'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4F63D2'}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Tipo
          </button>
        }
      />

      {/* Formulario nuevo/editar */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="15" height="15" fill="none" stroke="#4F63D2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              {editing ? 'Editar Tipo de Trámite' : 'Nuevo Tipo de Trámite'}
            </span>
          </div>
          <div style={{ padding: 20 }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 16px' }}>
                <div style={{ marginBottom: 14 }}>
                  <Label required>Nombre</Label>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <Label>Descripción</Label>
                  <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={submitting}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Guardando...' : 'Guardar'}
                </button>
                <button type="button" onClick={handleCancel}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Formulario duplicar */}
      {duplicating && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="15" height="15" fill="none" stroke="#4F63D2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              Duplicar "{duplicating.name}"
            </span>
          </div>
          <div style={{ padding: 20 }}>
            <form onSubmit={handleSubmitDuplicate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0 16px' }}>
                <div style={{ marginBottom: 14 }}>
                  <Label required>Nombre</Label>
                  <input type="text" value={duplicateForm.name} onChange={e => setDuplicateForm({ ...duplicateForm, name: e.target.value })} required style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <Label>Descripción</Label>
                  <input type="text" value={duplicateForm.description} onChange={e => setDuplicateForm({ ...duplicateForm, description: e.target.value })} style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <Label>Documentos a incluir</Label>
                {!duplicating.document_checklists?.length ? (
                  <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>Este tipo no tiene documentos</p>
                ) : (
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                    {sortByOrder(duplicating.document_checklists).map((doc, idx) => (
                      <label key={doc.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', fontSize: 13, color: '#374151', cursor: 'pointer', borderBottom: idx < duplicating.document_checklists.length - 1 ? '1px solid #F3F4F6' : 'none', backgroundColor: duplicateSelected[doc.id] ? '#fff' : '#F9FAFB' }}>
                        <input type="checkbox" checked={!!duplicateSelected[doc.id]} onChange={() => toggleDuplicateDoc(doc.id)} style={{ accentColor: '#4F63D2' }} />
                        <span style={{ flex: 1, fontWeight: 500, color: duplicateSelected[doc.id] ? '#111827' : '#9CA3AF' }}>{doc.name}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: doc.required ? '#FEE2E2' : '#F3F4F6', color: doc.required ? '#991B1B' : '#374151' }}>
                          {doc.required ? 'Requerido' : 'Opcional'}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={submitting}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Duplicando...' : 'Duplicar'}
                </button>
                <button type="button" onClick={handleCancelDuplicate}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de tipos */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
            <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          </div>
        ) : types.length === 0 ? (
          <EmptyState icon="📋" title="No hay tipos de trámite" subtitle="Crea el primer tipo para usarlo en expedientes" />
        ) : (
          types.map((type, idx) => (
            <div key={type.id} style={{ borderBottom: idx < types.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
              {/* Fila del tipo */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4F63D2', flexShrink: 0 }} />
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{type.name}</span>
                    {type.description && <span style={{ fontSize: 12, color: '#9CA3AF', marginLeft: 8 }}>{type.description}</span>}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2', flexShrink: 0 }}>
                    {type.document_checklists?.length || 0} doc{type.document_checklists?.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                  <button onClick={() => setExpandedStatusType(expandedStatusType === type.id ? null : type.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, border: '1.5px solid #EDE9FE', backgroundColor: expandedStatusType === type.id ? '#EDE9FE' : '#F5F3FF', color: '#6D28D9', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#EDE9FE'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = expandedStatusType === type.id ? '#EDE9FE' : '#F5F3FF'}>
                    <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expandedStatusType === type.id ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                    </svg>
                    Estatus
                  </button>
                  <button onClick={() => setExpandedType(expandedType === type.id ? null : type.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, border: '1.5px solid #DBEAFE', backgroundColor: expandedType === type.id ? '#DBEAFE' : '#EFF6FF', color: '#1D4ED8', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#DBEAFE'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = expandedType === type.id ? '#DBEAFE' : '#EFF6FF'}>
                    <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={expandedType === type.id ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                    </svg>
                    Checklist
                  </button>
                  <IconBtn onClick={() => handleDuplicate(type)} title="Duplicar" variant="info">
                    <DuplicateIcon />
                  </IconBtn>
                  <IconBtn onClick={() => handleEdit(type)} title="Editar">
                    <EditIcon />
                  </IconBtn>
                  <IconBtn onClick={() => handleDelete(type.id)} title="Desactivar" variant="danger">
                    <TrashIcon />
                  </IconBtn>
                </div>
              </div>

              {/* Estatus expandido */}
              {expandedStatusType === type.id && (
                <div style={{ borderTop: '1px solid #F3F4F6', backgroundColor: '#FAF9FF', padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Etapas del Trámite</span>
                    <button onClick={() => handleNewStatus(type.id)}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, backgroundColor: '#6D28D9', color: '#fff', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#5b21b6'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = '#6D28D9'}>
                      <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                      </svg>
                      Agregar Estatus
                    </button>
                  </div>

                  <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 12px' }}>
                    "Cancelado" no aparece aquí — sigue disponible siempre en cualquier expediente de este tipo. Solo la 1ª etapa avanza sola a la 2ª (según el checklist marcado "Necesario para avanzar"); el resto se cambia manualmente.
                  </p>

                  {/* Formulario estatus */}
                  {showStatusForm === type.id && (
                    <div style={{ background: '#fff', borderRadius: 9, border: '1px solid #E5E7EB', padding: 14, marginBottom: 12 }}>
                      <form onSubmit={e => handleSubmitStatus(e, type.id)}>
                        <div style={{ marginBottom: 12 }}>
                          <Label required>Nombre de la etapa</Label>
                          <input type="text" value={statusForm.name} onChange={e => setStatusForm({ ...statusForm, name: e.target.value })} required style={smInput} onFocus={focusSt} onBlur={blurSt} />
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="submit" disabled={submitting}
                            style={{ padding: '6px 14px', borderRadius: 7, border: 'none', backgroundColor: '#6D28D9', color: '#fff', fontWeight: 600, fontSize: 12, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                            {submitting ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button type="button" onClick={handleCancelStatus}
                            style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                            Cancelar
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Lista de estatus */}
                  {!type.statuses?.length ? (
                    <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>Este tipo no tiene etapas configuradas</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: 9, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F9FAFB' }}>
                          {['', '#', 'Etapa', ''].map((h, i) => (
                            <th key={i} style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortByOrder(type.statuses).map((item, sIdx) => (
                          <tr key={item.id}
                            style={{ borderBottom: '1px solid #F9FAFB', backgroundColor: dragStatusItem?.itemId === item.id ? '#EDE9FE' : '' }}
                            onDragOver={e => e.preventDefault()}
                            onDrop={() => handleDropStatus(type.id, item.id)}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = dragStatusItem?.itemId === item.id ? '#EDE9FE' : '#FAFBFF'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = dragStatusItem?.itemId === item.id ? '#EDE9FE' : ''}>
                            <td style={{ padding: '9px 6px', width: 24 }}>
                              <span
                                draggable
                                onDragStart={() => setDragStatusItem({ typeId: type.id, itemId: item.id })}
                                onDragEnd={() => setDragStatusItem(null)}
                                title="Arrastrar para reordenar"
                                style={{ display: 'inline-flex', color: '#C4C9D4', cursor: 'grab' }}>
                                <GripIcon />
                              </span>
                            </td>
                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#9CA3AF', fontWeight: 600 }}>{sIdx + 1}</td>
                            <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 500, color: '#111827' }}>{item.name}</td>
                            <td style={{ padding: '9px 12px' }}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                <IconBtn onClick={() => handleEditStatus(item, type.id)} title="Editar">
                                  <EditIcon />
                                </IconBtn>
                                <IconBtn onClick={() => handleDeleteStatus(type.id, item.id)} title="Eliminar" variant="danger">
                                  <TrashIcon size={11} />
                                </IconBtn>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Checklist expandido */}
              {expandedType === type.id && (
                <div style={{ borderTop: '1px solid #F3F4F6', backgroundColor: '#F9FAFB', padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Documentos del Checklist</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => handleSyncChecklist(type.id)} disabled={syncing === type.id}
                        title='Aplica "Necesario para avanzar" del checklist a los expedientes que ya existían antes de marcarlo'
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', color: '#374151', fontSize: 12, fontWeight: 600, cursor: syncing === type.id ? 'not-allowed' : 'pointer', opacity: syncing === type.id ? 0.6 : 1 }}
                        onMouseEnter={e => { if (syncing !== type.id) e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                        <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ animation: syncing === type.id ? 'spin 0.7s linear infinite' : 'none' }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {syncing === type.id ? 'Sincronizando...' : 'Sincronizar con expedientes existentes'}
                      </button>
                      <button onClick={() => handleNewChecklist(type.id)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 7, backgroundColor: '#4F63D2', color: '#fff', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d4fb8'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4F63D2'}>
                        <svg width="10" height="10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                        </svg>
                        Agregar Documento
                      </button>
                    </div>
                  </div>

                  {/* Formulario checklist */}
                  {showChecklistForm === type.id && (
                    <div style={{ background: '#fff', borderRadius: 9, border: '1px solid #E5E7EB', padding: 14, marginBottom: 12 }}>
                      <form onSubmit={e => handleSubmitChecklist(e, type.id)}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 10px' }}>
                          <div style={{ marginBottom: 10 }}>
                            <Label required>Nombre</Label>
                            <input type="text" value={checklistForm.name} onChange={e => setChecklistForm({ ...checklistForm, name: e.target.value })} required style={smInput} onFocus={focusSt} onBlur={blurSt} />
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <Label>Descripción</Label>
                            <input type="text" value={checklistForm.description} onChange={e => setChecklistForm({ ...checklistForm, description: e.target.value })} style={smInput} onFocus={focusSt} onBlur={blurSt} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#4B5563', cursor: 'pointer' }}>
                            <input type="checkbox" checked={checklistForm.required}
                              onChange={e => setChecklistForm({ ...checklistForm, required: e.target.checked, required_for_advance: e.target.checked ? checklistForm.required_for_advance : false })}
                              style={{ accentColor: '#4F63D2' }} />
                            Documento obligatorio
                          </label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: checklistForm.required ? '#4B5563' : '#C4C9D4', cursor: checklistForm.required ? 'pointer' : 'not-allowed' }}>
                            <input type="checkbox" checked={checklistForm.required_for_advance} disabled={!checklistForm.required}
                              onChange={e => setChecklistForm({ ...checklistForm, required_for_advance: e.target.checked })}
                              style={{ accentColor: '#4F63D2' }} />
                            Necesario para avanzar
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="submit" disabled={submitting}
                            style={{ padding: '6px 14px', borderRadius: 7, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 12, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                            {submitting ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button type="button" onClick={handleCancelChecklist}
                            style={{ padding: '6px 12px', borderRadius: 7, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
                            Cancelar
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Lista checklist */}
                  {!type.document_checklists?.length ? (
                    <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>No hay documentos en el checklist</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', borderRadius: 9, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#F9FAFB' }}>
                          {['', 'Documento', 'Descripción', 'Obligatorio', ''].map((h, i) => (
                            <th key={i} style={{ padding: '8px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortByOrder(type.document_checklists).map(item => (
                          <tr key={item.id}
                            style={{ borderBottom: '1px solid #F9FAFB', backgroundColor: dragItem?.itemId === item.id ? '#EEF0FB' : '' }}
                            onDragOver={e => e.preventDefault()}
                            onDrop={() => handleDrop(type.id, item.id)}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = dragItem?.itemId === item.id ? '#EEF0FB' : '#FAFBFF'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = dragItem?.itemId === item.id ? '#EEF0FB' : ''}>
                            <td style={{ padding: '9px 6px', width: 24 }}>
                              <span
                                draggable
                                onDragStart={() => setDragItem({ typeId: type.id, itemId: item.id })}
                                onDragEnd={() => setDragItem(null)}
                                title="Arrastrar para reordenar"
                                style={{ display: 'inline-flex', color: '#C4C9D4', cursor: 'grab' }}>
                                <GripIcon />
                              </span>
                            </td>
                            <td style={{ padding: '9px 12px', fontSize: 13, fontWeight: 500, color: '#111827' }}>{item.name}</td>
                            <td style={{ padding: '9px 12px', fontSize: 12, color: '#9CA3AF' }}>{item.description || '—'}</td>
                            <td style={{ padding: '9px 12px' }}>
                              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: item.required ? '#FEE2E2' : '#F3F4F6', color: item.required ? '#991B1B' : '#374151' }}>
                                {item.required ? 'Requerido' : 'Opcional'}
                              </span>
                              {item.required_for_advance && (
                                <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, backgroundColor: '#EEF0FB', color: '#4F63D2' }}>
                                  → Avanza etapa
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '9px 12px' }}>
                              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                <IconBtn onClick={() => handleEditChecklist(item, type.id)} title="Editar">
                                  <EditIcon />
                                </IconBtn>
                                <IconBtn onClick={() => handleDeleteChecklist(type.id, item.id)} title="Eliminar" variant="danger">
                                  <TrashIcon size={11} />
                                </IconBtn>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProcedureTypes;
